// src/theme.js
import { createTheme, alpha, lighten } from "@mui/material/styles";
import { esES as coreEsES } from "@mui/material/locale";
import { esES as gridEsES } from "@mui/x-data-grid/locales";

export const theme = createTheme(
  {
    shape: { borderRadius: 10 },

    palette: {
      primary: { main: "#41b6e6", contrastText: "#ffffff" },
      text: { primary: "#1f2a37", secondary: "#61869f" },
      background: { paper: "#f6f7fb", default: "#ffffff" },
    },

    typography: {
      fontFamily:
        "century-gothic, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
      fontWeightMedium: 500,
      fontWeightBold: 700,
    },

    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { backgroundColor: "#ffffff" },

          /* =========================
             Layout semántico reutilizable (ÚNICO)
             ========================= */

          /**
           * pageContainer
           * - Wrapper exterior de página
           * - IMPORTANTE: fuerza alineación izquierda (tu CSS legacy centra el body)
           */
          ".pageContainer": {
            width: "100%",
            boxSizing: "border-box",

            /* ✅ replica el “margen visual” de tus listados */
            padding: "16px 24px",

            /* ✅ anula el text-align:center heredado del body legacy */
            textAlign: "left",
          },

          /**
           * pageHeader
           * - Cabecera homogénea para TODAS las páginas
           * - Debe parecerse a “Jornadas Finalizadas”
           */
          ".pageHeader": {
            margin: "0 0 16px 0",
          },

          ".pageHeader__title": {
            margin: 0,
            fontWeight: 700,

            /* ✅ más parecido a tu captura de “Jornadas Finalizadas” */
            fontSize: "1.75rem",
            lineHeight: 1.15,

            color: "#1f2a37",
            letterSpacing: "-0.01em",
          },

          ".pageHeader__subtitle": {
            marginTop: 6,
            marginBottom: 0,
            color: "#61869f",
            fontSize: "0.95rem",
            lineHeight: 1.35,
          },

          /**
           * contentContainer
           * - Contenedor que alinea contenido con un ancho máximo
           * - (Útil para DataGrid / forms)
           */
          ".contentContainer": {
            width: "100%",
            maxWidth: 1280,
            margin: "0 auto",
            boxSizing: "border-box",
          },

          /* Variante para formularios (estrecha y centrada) */
          ".contentContainer--form": {
            maxWidth: 840,
          },

          /* Variante para formularios de 1 columna (más estrecha) */
          ".contentContainer--formSingle": {
            maxWidth: 560, // ajusta 520–620 según tu gusto
            width: "100%",
            alignSelf: "center", // 🔑 centra en el eje horizontal dentro de Stack (flex)
            boxSizing: "border-box",
          },

          /* Variante para “tabla compacta / estado” (Finalizar Jornada / Finalizar Intervención) */
          ".contentContainer--compactTable": {
            maxWidth: 720, // prueba 680–820 según gusto
            width: "100%",
            marginLeft: "auto",
            marginRight: "auto",
          },

          ".contentContainer--full": {
            width: "100%",
            maxWidth: "none", // ⬅️ para pantallas grandes: no “encajona”
            margin: 0, // ⬅️ evita centrado visual
            boxSizing: "border-box",
          },

          /* Paper de formulario con padding consistente */
          ".formPaper": {
            padding: 24,
          },

          /* Acciones de formulario alineadas a la derecha */
          ".formActions": {
            display: "flex",
            justifyContent: "flex-end",
            gap: 16,
            marginTop: 8,
            flexWrap: "wrap",
          },

          /* Grid de filtros (patrón repetido en listados) */
          ".filtersGrid": {
            display: "grid",
            gap: 16,
            alignItems: "end",
            gridTemplateColumns: "1fr",
          },

          ".row--disabled": { opacity: 0.55 },

          /* =========================
          Footer (compacto y coherente)
          ========================= */
        ".footer": {
          backgroundColor: "#41b6e6",
          borderTop: "1px solid rgba(255,255,255,0.35)",

          minHeight: 44,
          padding: "0 16px",

          display: "flex",
          alignItems: "center",
          justifyContent: "center",

          textAlign: "center",
          color: "#ffffff",

          fontSize: "0.85rem",
          fontWeight: 500,

          // ✅ anula el legacy si aún existe
          lineHeight: "normal",
        },

        ".footer a": {
          color: "#ffffff",
          textDecoration: "none",
          fontWeight: 600,
        },

        ".footer a:hover": {
          textDecoration: "underline",
        },

          /* Responsive para filtersGrid sin depender de sx */
          "@media (min-width:600px)": {
            ".filtersGrid": {
              gridTemplateColumns: "1fr 1fr",
            },
          },
          "@media (min-width:900px)": {
            ".filtersGrid": {
              gridTemplateColumns: "2fr 1fr 1fr 1fr auto",
            },
          },

          /* =========================
             Navbar (react-bootstrap) – Compacto
             =========================
             Objetivo:
             - Neutralizar CSS legacy agresivo (.navi li / .navi a con padding grande y !important)
             - Mantener dropdowns legibles y compactos
             - Conservar estética (azul + blanco) y alineación
           */

          /* Contenedor navbar */
          ".navi": {
            minHeight: 44,
            paddingTop: 0,
            paddingBottom: 0,
          },

          /* El legacy mete padding en li */
          ".navi li": {
            padding: "0 !important",
          },

          /* Links y toggles */
          ".navi .nav-link, .navi .dropdown-toggle": {
            padding: "6px 10px !important",
            fontSize: "1rem",
            fontWeight: 600,
            lineHeight: 1.15,
            textTransform: "uppercase",
            display: "flex",
            alignItems: "center",
            color: "#ffffff",
            textDecoration: "none",
          },

          /* Hover/active suaves (sin inventar colores) */
          ".navi .nav-link:hover, .navi .dropdown-toggle:hover": {
            opacity: 0.95,
          },

          /* Active link (NavLink aplica .active en react-bootstrap links) */
          ".navi .nav-link.active": {
            backgroundColor: "rgba(255,255,255,0.16)",
            borderRadius: 8,
          },

          /* Dropdown menú */
          ".navi .dropdown-menu": {
            paddingTop: 6,
            paddingBottom: 6,
            borderRadius: 10,
          },

          ".navi .dropdown-item": {
            padding: "6px 14px",
            fontSize: "0.9rem",
          },

          /* Usuario derecha (chip) */
          ".nav-user-chip": {
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
          },

          ".nav-user-name": {
            fontSize: "0.9rem",
            fontWeight: 700,
            textTransform: "uppercase",
            lineHeight: 1.1,
            whiteSpace: "nowrap",
          },

          ".avatar-navbar": {
            width: 28,
            height: 28,
            borderRadius: "50%",
            overflow: "hidden",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          },

          ".avatar-navbar img": {
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          },

          ".avatar-fallback-navbar": {
            width: 28,
            height: 28,
            borderRadius: "50%",
            background: "rgba(255,255,255,.25)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.75rem",
            fontWeight: 800,
            lineHeight: 1,
            color: "#ffffff",
          },
        },
      },


      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: "#f6f7fb",
            border: "1px solid rgba(0,0,0,0.06)",
            borderRadius: 12,
          },
        },
      },

      MuiTextField: {
        defaultProps: { size: "small", fullWidth: true },
      },

      MuiButton: {
        defaultProps: {
          disableElevation: true,
          variant: "contained",
          color: "primary",
        },
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 8,
            paddingLeft: 16,
            paddingRight: 16,
          },
        },
      },

      MuiDialog: {
        defaultProps: { fullWidth: true, maxWidth: "sm" },
      },

      MuiChip: {
        defaultProps: { size: "small" },
        styleOverrides: {
          root: { fontWeight: 600 },
          filledSuccess: ({ theme }) => ({
            backgroundColor: lighten(theme.palette.success.main, 0.82),
            color: theme.palette.text.primary,
          }),
          filledError: ({ theme }) => ({
            backgroundColor: lighten(theme.palette.error.main, 0.85),
            color: theme.palette.text.primary,
          }),
          filledDefault: ({ theme }) => ({
            backgroundColor: alpha(theme.palette.text.primary, 0.06),
            color: theme.palette.text.primary,
          }),
        },
      },

      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: "none",
          },

          columnHeaders: {
            backgroundColor: "#f6f7fb", // cabecera gris suave
            fontWeight: 600,
          },

          row: {
            backgroundColor: "#ffffff", // ⬅️ filas blancas
          },

          cell: {
            display: "flex",
            alignItems: "center",
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: "#ffffff", // ⬅️ input blanco
            borderRadius: 8,
          },
        },
      },

      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            backgroundColor: "#ffffff", // ⬅️ lista desplegable blanca
          },
        },
      },
    },

    /* =========================
       Kanban UI (reutilizable)
       ========================= */
    ".kanbanBoard": {
      display: "grid",
      gap: 16,
      gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
      alignItems: "start",
    },

    ".kanbanColumn": {
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
    },

    ".kanbanColumn__paper": {
      padding: 12,
      display: "flex",
      flexDirection: "column",
      gap: 10,
      backgroundColor: "#f6f7fb",
    },

    ".kanbanColumn__header": {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      padding: "2px 4px 8px 4px",
      borderBottom: "1px solid rgba(0,0,0,0.06)",
    },

    ".kanbanColumn__title": {
      fontWeight: 700,
      fontSize: "0.95rem",
      color: "#1f2a37",
    },

    ".kanbanColumn__count": {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 26,
      height: 22,
      padding: "0 8px",
      borderRadius: 999,
      fontWeight: 700,
      fontSize: "0.8rem",
      backgroundColor: "rgba(0,0,0,0.06)",
    },

    ".kanbanColumn__body": {
      display: "flex",
      flexDirection: "column",
      gap: 12,
      paddingTop: 10,
      minHeight: 120,
    },

    ".kanbanColumn__body--active": {
      outline: "2px dashed rgba(65, 182, 230, 0.55)",
      outlineOffset: 6,
      borderRadius: 12,
    },

    ".kanbanCard": {
      backgroundColor: "#ffffff",
      border: "1px solid rgba(0,0,0,0.08)",
      borderRadius: 12,
      padding: 12,
      boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
      width: "100%",
      boxSizing: "border-box",
    },

    ".kanbanCard--dragging": {
      boxShadow: "0 10px 24px rgba(0,0,0,0.14)",
    },
  },
  coreEsES,
  gridEsES
);
